import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { LinkIcon } from '@heroicons/react/24/outline';
import connectApi from '../../../services/connectApi';
import { headerCls, DataTableSkeleton } from '../../../data/columns';
import { TWDataTable, TWButton, EmptyState, UserAvatar } from '../../../components/ui';
import FinBadge from '../../connect/finance/fin-badge';
import { RoutingLabel } from '../../company/company-detail/company-routing-badge';
import { isReadyToActivate } from '../../../utils/connectModel';

// The admin Finance page's Connect accounts (owner, 2026-09-24): every
// company with a Stripe connected account, with the same three facts the
// restaurant's own Business Details strip shows — in the same words, so an
// admin and a restaurant reading the same account read the same thing —
// and which way its money routes. One read for the whole list, off the
// API's webhook-kept columns; nothing here asks Stripe per row.
const Fact = ({ ok, okLabel, notLabel }) => (
  <FinBadge tone={ok ? 'available' : 'pending'} label={ok ? okLabel : notLabel} dot={ok} />
);

// A company that has not started Connect (owner, 2026-09-24): listed so the
// admin sees every company in one place, with nothing claimed about it.
const NotStarted = () => <FinBadge tone="canceled" label="Not started" />;

const ConnectAccountsTable = () => {
  const [state, setState] = useState({ rows: [], loading: true, error: '' });

  const load = async () => {
    setState((prev) => ({ ...prev, loading: true, error: '' }));
    try {
      const rows = connectApi.toConnectAccounts(await connectApi.getConnectAccounts());
      setState({ rows, loading: false, error: '' });
    } catch {
      setState({ rows: [], loading: false, error: "Couldn't load the Connect accounts." });
    }
  };

  useEffect(() => {
    load();
  }, []);

  const columns = [
    {
      title: <span className={headerCls}>Company</span>,
      key: 'company',
      render: (_, row) => (
        <div className="flex min-w-0 items-center gap-3">
          {row.logo ? (
            <img
              alt="logo"
              className="h-9 w-9 shrink-0 rounded-md object-cover ring-1 ring-slate-200 dark:ring-slate-700"
              src={row.logo}
            />
          ) : (
            <UserAvatar name={row.companyName} size="sm" className="!rounded-md" />
          )}
          <div className="min-w-0 flex-1">
            <Link
              to={`/dashboard/companies/${row.companyId}?tab=connect`}
              className="truncate text-sm font-semibold text-slate-900 hover:text-blue-600 dark:text-slate-100 dark:hover:text-sky-400"
            >
              {row.companyName}
            </Link>
            <p className="truncate text-xs text-slate-500 dark:text-slate-400">
              {row.hasAccount ? row.stripeAccountId : 'No Stripe account yet'}
            </p>
          </div>
        </div>
      ),
    },
    {
      title: <span className={headerCls}>Routing</span>,
      key: 'routing',
      render: (_, row) => (
        <span className="flex flex-wrap items-center gap-1.5">
          <RoutingLabel model={row.paymentModel} />
          {/* Owner, 2026-09-25: every company goes live through Activate;
              this says Stripe's side is done. Terminals are Activate's own
              blockers and are not part of it. */}
          {isReadyToActivate(row) ? <FinBadge tone="available" label="Ready to activate" dot /> : null}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Taking payments</span>,
      key: 'charges',
      render: (_, row) => (row.hasAccount ? <Fact ok={row.chargesEnabled} okLabel="Active" notLabel="Not yet" /> : <NotStarted />),
    },
    {
      title: <span className={headerCls}>Receiving payouts</span>,
      key: 'payouts',
      render: (_, row) => (row.hasAccount ? <Fact ok={row.payoutsEnabled} okLabel="Active" notLabel="Not yet" /> : <NotStarted />),
    },
    {
      title: <span className={headerCls}>Verification</span>,
      key: 'verification',
      render: (_, row) =>
        row.hasAccount ? (
          <Fact
            ok={row.outstanding === 0}
            okLabel="Complete"
            notLabel={`${row.outstanding} ${row.outstanding === 1 ? 'item' : 'items'} outstanding`}
          />
        ) : (
          <NotStarted />
        ),
    },
  ];

  return (
    <section className="space-y-4" aria-labelledby="connect-accounts-heading">
      <div>
        <h2 id="connect-accounts-heading" className="text-base font-bold text-slate-900 dark:text-slate-100">
          Connect accounts
        </h2>
        <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
          Every company: whether it has a Stripe account yet, and whether Stripe is taking its payments and paying it out.
        </p>
      </div>
      {state.loading ? (
        <DataTableSkeleton
          rows={4}
          headers={[
            { label: 'Company', w: 'w-40', shape: 'avatar' },
            { label: 'Routing', w: 'w-24', shape: 'pill' },
            { label: 'Taking payments', w: 'w-20', shape: 'pill' },
            { label: 'Receiving payouts', w: 'w-20', shape: 'pill' },
            { label: 'Verification', w: 'w-24', shape: 'pill' },
          ]}
        />
      ) : state.error ? (
        <div className="flex flex-wrap items-center gap-3 rounded-lg border border-slate-200 px-4 py-3 dark:border-slate-700">
          <p className="text-sm text-rose-600 dark:text-rose-400">{state.error}</p>
          <TWButton size="sm" label="Try again" onClick={load} />
        </div>
      ) : state.rows.length === 0 ? (
        <EmptyState
          icon={LinkIcon}
          title="No companies yet"
          description="Every live company appears here, with or without a Stripe account."
        />
      ) : (
        <TWDataTable columns={columns} data={state.rows} rowKey="companyId" pagination={false} />
      )}
    </section>
  );
};

export default ConnectAccountsTable;
